import Avatar from '@hellodarwin/core/lib/components/common/avatar';
import Link from '@hellodarwin/core/lib/components/common/link';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import GrantCardStatusList from '@hellodarwin/core/lib/components/grants/card/grant-card-status-list';
import useDarwinLogoFallback from '@hellodarwin/core/lib/components/provider/profile/hooks/use-darwin-picture-fallback';
import type { GrantResult } from '@hellodarwin/core/lib/features/entities/grants-entities';
import type { Program } from '@hellodarwin/core/lib/features/entities/programs-entities';
import type { ProgramAdmin } from '@hellodarwin/core/lib/features/entities';
import { dollarFormat } from '@hellodarwin/core/lib/features/helpers';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import ExternalLink from '@hellodarwin/icons/dist/icons/NewWindow';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../../../app/app-hooks';
import { selectGrantById } from '../../../../../features/api/slices/programs-slice';
import ApplicationTeamSummary from './application-team-summary';
import {
  ContentDetails,
  MainContentHeaderActions,
  LinkContent,
  ResponsiveActionsWrapper,
  ResponsiveContentWrapper,
  ResponsiveHeaderContainer,
  ResponsiveLinkWrapper,
} from './styles';

interface GrantHeaderContentProps {
  grantId: string;
  grantStatus?: string;
  title?: string;
  program?: Program;
  programAdmins?: ProgramAdmin[];
  headerAction?: ReactNode;
}

const EligibleFinancing = ({
  funding_min_amount,
  funding_max_amount,
  percentage_funding,
}: {
  funding_min_amount?: number | null;
  funding_max_amount?: number | null;
  percentage_funding?: number | null;
}) => {
  const { selectedLocale } = useLocale();
  const { t } = useTranslations();
  const lines: string[] = [];
  if (!!funding_min_amount && !!funding_max_amount) {
    lines.push(
      t('grant_card|fromMinToMax', {
        min: `${dollarFormat(funding_min_amount, selectedLocale)}`,
        max: `${dollarFormat(funding_max_amount, selectedLocale)}`,
      }),
    );
  } else if (!!funding_min_amount) {
    lines.push(
      t('grant_card|minCount2', {
        minCount: `${dollarFormat(funding_min_amount, selectedLocale)}`,
      }),
    );
  } else if (!!funding_max_amount) {
    lines.push(
      t('grant_card|maxCount2', {
        maxCount: `${dollarFormat(funding_max_amount, selectedLocale)}`,
      }),
    );
  }
  if (!!percentage_funding) {
    lines.push(
      t('grant_card|projectCostPercent', {
        percent: `${percentage_funding}`,
      }),
    );
  }
  if (!lines.length) {
    lines.push(t('grant_card|noCondition'));
  }

  return (
    <MainContentHeaderActions>
      {lines.map((text, idx) => (
        <Typography key={idx} elementTheme="body2">
          {text}
        </Typography>
      ))}
    </MainContentHeaderActions>
  );
};

const MainContentHeader = ({
  grantId,
  program,
  grantStatus = 'closed',
  title,
  headerAction,
  programAdmins = [],
}: GrantHeaderContentProps) => {
  const grant = useAppSelector(
    (state) => selectGrantById(state, grantId) as GrantResult | undefined,
  );
  const fallback = useDarwinLogoFallback({ type: 'grant-provider', size: 96 });
  const contentRef = useRef<HTMLDivElement>(null);
  const [avatarSize, setAvatarSize] = useState(30);
  const { t } = useTranslations();

  useEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.offsetHeight;
      setAvatarSize(height);
    }
  }, [grant, title, program?.program_name]);

  const logo = grant?.grant_logo;
  const resolvedTitle =
    title || program?.program_name || grant?.grant_display_title || '';
  const application_status =
    grantStatus || (grant as any)?.application_status || 'closed';
  const featuredTags: string[] = [];

  if (!resolvedTitle) return null;

  return (
    <ResponsiveHeaderContainer>
      <ResponsiveContentWrapper>
        <Avatar
          src={logo}
          alt={`${resolvedTitle}`}
          fallback={fallback}
          size={avatarSize}
          shape="rounded-corner"
          style={{ aspectRatio: '1/1' }}
        >
          {resolvedTitle?.[0] || ''}
        </Avatar>

        <ContentDetails ref={contentRef}>
          <GrantCardStatusList
            featuredTags={featuredTags}
            application_status={application_status}
          />

          <Typography.Title level={3} elementTheme="h5">
            {resolvedTitle}
          </Typography.Title>

          {grant ? (
            <EligibleFinancing
              funding_min_amount={grant?.funding_min_amount as any}
              funding_max_amount={grant?.funding_max_amount as any}
              percentage_funding={grant?.percentage_funding as any}
            />
          ) : null}
        </ContentDetails>
      </ResponsiveContentWrapper>
      <ResponsiveActionsWrapper>
        {headerAction}
        {grant?.grant_id ? (
          <ResponsiveLinkWrapper>
            <Link to={`/funding-explorer/${grant.grant_id}`} target="_blank">
              <LinkContent>
                <Typography fitContent elementTheme="body1">
                  {t('grant_card|goToGrant')}
                </Typography>
                <ExternalLink style={{ marginLeft: '8px' }} size={20} />
              </LinkContent>
            </Link>
          </ResponsiveLinkWrapper>
        ) : null}
        {programAdmins.length > 0 ? (
          <ApplicationTeamSummary programAdmins={programAdmins} />
        ) : null}
      </ResponsiveActionsWrapper>
    </ResponsiveHeaderContainer>
  );
};

export default MainContentHeader;
